import { useCallback, useState } from 'react';
import { DueDateOverride } from '../../../lib/api/settlementDateAdjustment';

type UseDueDateOverrideMapResult = {
  /**
   * 키는 settlementItemId, 값은 운영자가 직접 지정한 dueDate(YYYY-MM-DD).
   * Map 에 없는 행은 서버 자동 계산값을 그대로 사용한다.
   */
  overrideMap: Map<number, string>;
  /**
   * 행 단위 dueDate 변경. `dueDate` 가 null 이면 override 를 제거(자동 계산값 복원).
   */
  setOverride: (settlementItemId: number, dueDate: string | null) => void;
  /** 모달 닫기/Preview 새로 받기/Execute 성공 시 모두 초기화. */
  resetOverrides: () => void;
  /** Execute payload 로 직렬화 — 비어있으면 undefined 로 필드 자체 생략. */
  toPayload: () => DueDateOverride[] | undefined;
};

/**
 * PreviewModal 의 "변경 후 정산 예정일" 셀에서 행별로 직접 지정한 dueDate 를 관리.
 *
 * 컨벤션: 상태/핸들러/직렬화 로직이 응집되어 있어 ExecuteTabPanel 의 뷰 코드에서 분리.
 * 검증(영업일/baseDate 역전/대상 포함) 은 서버가 execute 시점에 fail-fast 로 처리하므로
 * 여기서는 클라이언트 측 입력만 보관한다.
 */
export const useDueDateOverrideMap = (): UseDueDateOverrideMapResult => {
  const [overrideMap, setOverrideMap] = useState<Map<number, string>>(new Map());

  // setOverride / resetOverrides 는 외부 의존성이 없어 마운트 시 1회만 생성되면 됨
  // (useState 의 setter 는 stable 이라 deps 비워도 안전).
  const setOverride = useCallback(
    (settlementItemId: number, dueDate: string | null): void => {
      setOverrideMap((prev) => {
        const next = new Map(prev);
        if (dueDate === null) {
          next.delete(settlementItemId);
        } else {
          next.set(settlementItemId, dueDate);
        }
        return next;
      });
    },
    [],
  );

  const resetOverrides = useCallback((): void => {
    setOverrideMap(new Map());
  }, []);

  // overrideMap 자체를 읽으므로 그 참조가 바뀔 때만 새 함수 생성.
  const toPayload = useCallback((): DueDateOverride[] | undefined => {
    if (overrideMap.size === 0) return undefined;
    return Array.from(overrideMap, ([settlementItemId, dueDate]) => ({
      settlementItemId,
      dueDate,
    }));
  }, [overrideMap]);

  return { overrideMap, setOverride, resetOverrides, toPayload };
};
